'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import type { TooltipProps } from 'recharts';

const ResponsiveContainer = dynamic(() => import('recharts').then((m) => m.ResponsiveContainer), { ssr: false });
const LineChart = dynamic(() => import('recharts').then((m) => m.LineChart), { ssr: false });
const Line = dynamic(() => import('recharts').then((m) => m.Line), { ssr: false });
const XAxis = dynamic(() => import('recharts').then((m) => m.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then((m) => m.YAxis), { ssr: false });
const CartesianGrid = dynamic(() => import('recharts').then((m) => m.CartesianGrid), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then((m) => m.Tooltip), { ssr: false });
const ReferenceLine = dynamic(() => import('recharts').then((m) => m.ReferenceLine), { ssr: false });

const btEquityData = [
  { month: 'Jan 25', equity: 25000 },
  { month: 'Feb 25', equity: 26840 },
  { month: 'Mar 25', equity: 24210 },
  { month: 'Apr 25', equity: 25980 },
  { month: 'May 25', equity: 28640 },
  { month: 'Jun 25', equity: 31200 },
  { month: 'Jul 25', equity: 29840 },
  { month: 'Aug 25', equity: 33410 },
  { month: 'Sep 25', equity: 36820 },
  { month: 'Oct 25', equity: 38140 },
  { month: 'Nov 25', equity: 35690 },
  { month: 'Dec 25', equity: 39240 },
  { month: 'Jan 26', equity: 41820 },
  { month: 'Feb 26', equity: 44160 },
  { month: 'Mar 26', equity: 42380 },
  { month: 'Apr 26', equity: 45920 },
  { month: 'May 26', equity: 48170 },
  { month: 'Jun 26', equity: 46840 },
  { month: 'Jul 26', equity: 47390 },
  { month: 'Aug 26', equity: 48170 },
];

function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0]?.value as number;
  const pnl = val - 25000;
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-600 font-mono-nums text-foreground">${val.toLocaleString()}</p>
      <p className={`text-xs font-mono-nums mt-0.5 ${pnl >= 0 ? 'text-positive' : 'text-negative'}`}>
        {pnl >= 0 ? '+' : ''}${pnl.toLocaleString()} P&L
      </p>
    </div>
  );
}

export default function BacktestEquityCurve() {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="mb-4">
        <h4 className="text-sm font-600 text-foreground">Equity Curve</h4>
        <p className="text-xs text-muted-foreground mt-0.5">Portfolio value over backtest period</p>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={btEquityData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              interval={3}
            />
            <YAxis
              tick={{ fill: 'var(--muted-foreground)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={25000} stroke="var(--border)" strokeDasharray="4 4" />
            <Line
              type="monotone"
              dataKey="equity"
              stroke="var(--primary)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: 'var(--primary)', stroke: 'var(--background)', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}